import { StarFilledIcon, Typography } from '@iheartjane/reefer';
import { useId, useState } from 'react';

import styles from './starRatingField.module.css';

export interface StarRatingFieldProps {
  /** Validation message rendered below the stars. */
  errorMessage?: string;
  /** Group label. Defaults to "Overall product rating". */
  legend?: string;
  /** Fired with the selected whole-star rating (1-5). */
  onChange: (rating: number) => void;
  /** `data-testid` on the wrapper. */
  testId?: string;
  /** Current rating. 0 means nothing is selected. */
  value: number;
}

const STAR_VALUES = [1, 2, 3, 4, 5];

export function StarRatingField({
  errorMessage,
  legend = 'Overall product rating',
  onChange,
  testId = 'star-rating-field',
  value,
}: StarRatingFieldProps) {
  const [previewValue, setPreviewValue] = useState(0);
  const errorId = useId();
  const legendId = useId();
  const radioGroupName = useId();

  const displayValue = previewValue || value;

  return (
    <fieldset
      aria-describedby={errorMessage ? errorId : undefined}
      aria-invalid={errorMessage ? true : undefined}
      aria-labelledby={legendId}
      data-testid={testId}
      role="radiogroup"
    >
      <Typography as="legend" id={legendId} variant="body-semibold">
        {legend}
      </Typography>
      <div className={styles['stars']} onMouseLeave={() => setPreviewValue(0)}>
        {STAR_VALUES.map((starValue) => (
          <label
            key={starValue}
            className={styles['star']}
            onMouseEnter={() => setPreviewValue(starValue)}
          >
            <input
              aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
              checked={value === starValue}
              className={styles['input']}
              name={radioGroupName}
              onChange={() => onChange(starValue)}
              type="radio"
              value={starValue}
            />
            <StarFilledIcon
              className={
                starValue <= displayValue
                  ? styles['starFilled']
                  : styles['starEmpty']
              }
              data-testid={
                starValue <= displayValue
                  ? `${testId}-star-filled`
                  : `${testId}-star-empty`
              }
              size="lg"
            />
          </label>
        ))}
      </div>
      {errorMessage && (
        <Typography color="error" id={errorId} mt={4}>
          {errorMessage}
        </Typography>
      )}
    </fieldset>
  );
}
